import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { buildElementOptionLabelResolver } from "../utils/masterHelper.js"
import { setMasterList } from "../redux/slices/masterDataSlice.js"
import { getMasterList } from "../services/adminServices.js"

// The condition pickers also run outside the form editor (workflow canvas), where nothing
// has loaded the master list yet. Fetched at most once per session: an empty list is a
// valid answer, so the guard is only cleared when the request itself fails.
let masterListRequest = null

// Resolves the display text of options baked onto master backed form elements.
// Their labels can hold canonical ids rather than text - see buildElementOptionLabelResolver.
export const useMasterOptionLabel = () => {
    const dispatch = useDispatch()
    const { masterList } = useSelector((state) => state.masterData)
    const { i18n } = useTranslation()

    useEffect(() => {
        if (masterList?.length || masterListRequest) {
            return
        }
        masterListRequest = getMasterList()
            .then((response) => {
                // Only a real list is published. `getMasterList` also answers with an empty
                // list when the tenant id is not in storage yet or the call failed, and
                // storing that would wipe the list a page owner (the form editor) fetched
                // in parallel - which every master backed dropdown reads for its labels.
                if (Array.isArray(response?.data) && response.data.length) {
                    dispatch(setMasterList(response.data))
                    return
                }
                masterListRequest = null
            })
            .catch(() => {
                masterListRequest = null
            })
    }, [masterList, dispatch])

    // One resolver per element: building the label maps walks every master, and a picker
    // asks for a label per option. The cache is dropped whenever the masters or the
    // language change, since both decide what a resolver returns.
    const cacheRef = useRef({ masterList: null, lng: null, resolvers: new Map() })

    const getOptionLabel = useCallback((element, option, lngOverride) => {
        const lng = lngOverride || i18n.language
        if (!element) {
            return option && typeof option === "object" ? (option.label ?? option.value) : option
        }
        const cache = cacheRef.current
        if (cache.masterList !== masterList || cache.lng !== lng) {
            cacheRef.current = { masterList, lng, resolvers: new Map() }
        }
        const { resolvers } = cacheRef.current
        let resolve = resolvers.get(element)
        if (!resolve) {
            resolve = buildElementOptionLabelResolver(element, masterList, lng)
            resolvers.set(element, resolve)
        }
        return resolve(option)
    }, [masterList, i18n.language])

    // A stored condition value is the option's value, so it reads its label off the element.
    // Master-backed fields store the row uuid; baked options can lag behind the live
    // table or carry that uuid as the label, so still ask the resolver with the raw value.
    // `lngOverride` pins the language (public share links) so a set-into-text writes
    // the same option label the form is currently showing.
    const getValueLabel = useCallback((element, value, lngOverride) => {
        if (value === undefined || value === null || value === "") {
            return value
        }
        const options = Array.isArray(element?.options) ? element.options : []
        const option = options.find((opt) => String(opt?.value ?? opt) === String(value))
        if (option) {
            return getOptionLabel(element, option, lngOverride)
        }
        if (element) {
            return getOptionLabel(element, { value, label: value }, lngOverride)
        }
        return value
    }, [getOptionLabel])

    return { getOptionLabel, getValueLabel }
}

export default useMasterOptionLabel
